import { arAcceptCeiling } from './validateAcceptedQuantity'

const PREC = 1000

const round = (value: number) => Math.round(value * PREC)
const unround = (value: number) => value / PREC

/**
 * Limita uma quantidade desejada ao saldo restante do teto da AR.
 * Nunca negativa, nunca acima do saldo. Math inteira escalada (anti float-drift).
 */
export const clampAcceptedToAr = (desired: number, remainingArBudget: number): number => {
  const desiredInt = round(desired)
  const budgetInt = Math.max(0, round(remainingArBudget))
  return unround(Math.max(0, Math.min(desiredInt, budgetInt)))
}

export type AcceptMode = 'WITH_DAMAGE' | 'USEFUL_ONLY'

export interface BatchAcceptInput {
  key: string;
  usefulQuantity: number;
  damageQuantity: number;
  /** Recebido no lote = util + avaria */
  totalQuantity: number;
  /** Validade do lote (yyyy-mm-dd); lote sem validade e o ultimo a ser devolvido */
  expirationDate?: string | null;
}

export interface BatchAcceptResult {
  key: string;
  acceptedQuantity: number;
  returnQuantity: number;
}

/**
 * Ordem em que os lotes recebem o saldo da AR, o inverso da ordem em que o excedente e
 * devolvido: lote sem validade primeiro (devolvido por ultimo), depois a validade mais
 * distante. Empate (inclusive entre lotes sem validade) fica a cargo da ordem da lista.
 */
const compareFillOrder = (a: BatchAcceptInput, b: BatchAcceptInput): number => {
  const aDate = a.expirationDate || ''
  const bDate = b.expirationDate || ''

  if (aDate === bDate) return 0
  if (!aDate) return -1
  if (!bDate) return 1
  return aDate > bDate ? -1 : 1
}

/**
 * Distribui o teto da AR (`expectedQuantity`) entre os lotes de um produto.
 * Cada lote recebe `min(desejado, saldo)`; o restante do lote vira devolucao.
 *
 * O que passa do teto e devolvido nesta ordem, independente da ordem dos lotes na lista:
 *   1. avaria antes de util (o util de todos os lotes e aceito antes de qualquer avaria)
 *   2. validade mais proxima antes da mais distante; lote sem validade por ultimo
 * Empates mantem a ordem da lista; o resultado segue a ordem de `batches`.
 *
 * - `WITH_DAMAGE`: desejado = util, depois avaria (Aceitar tudo)
 * - `USEFUL_ONLY`: desejado = util (Aceitar sem avarias)
 *
 * Produtos em kg (`isKg`) toleram ate KG_ACCEPT_TOLERANCE a mais que a AR.
 */
export const distributeAcceptedAcrossBatches = (
  batches: BatchAcceptInput[],
  expectedQuantity: number,
  mode: AcceptMode,
  isKg = false,
): BatchAcceptResult[] => {
  let remainingInt = Math.max(0, round(arAcceptCeiling(expectedQuantity, isKg)))
  const acceptedInt = batches.map(() => 0)

  const fillOrder = batches
    .map((batch, index) => ({ batch, index }))
    .sort((a, b) => compareFillOrder(a.batch, b.batch) || a.index - b.index)

  const fill = (desiredOf: (batch: BatchAcceptInput) => number) => {
    fillOrder.forEach(({ batch, index }) => {
      const takenInt = Math.max(0, Math.min(round(desiredOf(batch)), remainingInt))
      acceptedInt[index] += takenInt
      remainingInt -= takenInt
    })
  }

  fill((batch) => batch.usefulQuantity)
  if (mode === 'WITH_DAMAGE') fill((batch) => batch.damageQuantity)

  return batches.map((batch, index) => ({
    key: batch.key,
    acceptedQuantity: unround(acceptedInt[index]),
    returnQuantity: unround(round(batch.totalQuantity) - acceptedInt[index]),
  }))
}
